import React, { useEffect, useMemo, useRef, useState } from 'react';
import { unzipSync } from 'fflate';
import './DicomZipArchiveModal.scss';

type ZipEntryKind = 'dicom' | 'pdf' | 'unsupported';
type TreeNodeKind = 'folder' | 'file';

interface ZipViewerEntry {
  name: string;
  data: Uint8Array;
  kind: ZipEntryKind;
  size: number;
}

interface ZipTreeNode {
  id: string;
  name: string;
  kind: TreeNodeKind;
  depth: number;
  children: ZipTreeNode[];
  entry?: ZipViewerEntry;
}

interface ZipSummary {
  dicom: number;
  pdf: number;
  unsupported: number;
  size: number;
}

interface DicomZipArchiveModalProps {
  fileName: string;
  zipBlob: Blob;
  onClose: () => void;
}

declare global {
  interface Window {
    cornerstone?: any;
    cornerstoneWADOImageLoader?: any;
    dicomParser?: any;
  }
}

const DICOM_ASSET_PATH = '/assets/care_coordination_dicom.js';
const DICOM_EXTENSIONS = ['.dcm', '.dicom'];

const getExtension = (fileName: string) => {
  const normalized = fileName.toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex) : '';
};

const hasDicomPreamble = (data: Uint8Array) =>
  data.length > 132 &&
  data[128] === 68 &&
  data[129] === 73 &&
  data[130] === 67 &&
  data[131] === 77;

const getEntryKind = (name: string, data: Uint8Array): ZipEntryKind => {
  const extension = getExtension(name);
  if (extension === '.pdf') return 'pdf';
  if (DICOM_EXTENSIONS.includes(extension) || hasDicomPreamble(data)) return 'dicom';
  return 'unsupported';
};

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  const precision = value >= 10 || index === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[index]}`;
};

const collectSummary = (entries: ZipViewerEntry[]): ZipSummary => ({
  dicom: entries.filter((entry) => entry.kind === 'dicom').length,
  pdf: entries.filter((entry) => entry.kind === 'pdf').length,
  unsupported: entries.filter((entry) => entry.kind === 'unsupported').length,
  size: entries.reduce((total, entry) => total + entry.size, 0),
});

const getFolderEntries = (node: ZipTreeNode) => {
  const entries: ZipViewerEntry[] = [];
  const visit = (current: ZipTreeNode) => {
    if (current.entry) entries.push(current.entry);
    current.children.forEach(visit);
  };
  visit(node);
  return entries;
};

const getFolderSummaryLabel = (summary: ZipSummary) => {
  const parts = [];
  if (summary.dicom) parts.push(`${summary.dicom} DICOM`);
  if (summary.pdf) parts.push(`${summary.pdf} PDF`);
  if (summary.unsupported && !parts.length) parts.push(`${summary.unsupported} other`);
  return `${parts.join(' · ')}${parts.length ? ' · ' : ''}${formatBytes(summary.size)}`;
};

const buildTree = (entries: ZipViewerEntry[]) => {
  const root: ZipTreeNode = { id: '', name: '', kind: 'folder', depth: -1, children: [] };
  const folderMap = new Map<string, ZipTreeNode>([['', root]]);

  entries.forEach((entry) => {
    const parts = entry.name.split('/').filter(Boolean);
    let parent = root;
    let path = '';

    parts.forEach((part, index) => {
      const isFile = index === parts.length - 1;
      path = path ? `${path}/${part}` : part;

      if (isFile) {
        parent.children.push({
          id: entry.name,
          name: part,
          kind: 'file',
          depth: index,
          children: [],
          entry,
        });
        return;
      }

      let folder = folderMap.get(path);
      if (!folder) {
        folder = { id: path, name: part, kind: 'folder', depth: index, children: [] };
        folderMap.set(path, folder);
        parent.children.push(folder);
      }
      parent = folder;
    });
  });

  const sortTree = (node: ZipTreeNode) => {
    node.children.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
    node.children.forEach(sortTree);
  };

  sortTree(root);
  return root.children;
};

const getFolderIds = (nodes: ZipTreeNode[]) => {
  const ids: string[] = [];
  const visit = (node: ZipTreeNode) => {
    if (node.kind === 'folder') ids.push(node.id);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const filterTree = (nodes: ZipTreeNode[], query: string): ZipTreeNode[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return nodes;

  return nodes
    .map((node) => {
      const children = filterTree(node.children, normalizedQuery);
      const matches = node.name.toLowerCase().includes(normalizedQuery);
      if (!matches && children.length === 0) return null;
      return { ...node, children };
    })
    .filter((node): node is ZipTreeNode => Boolean(node));
};

const loadDicomAssets = () =>
  new Promise<void>((resolve, reject) => {
    if (window.cornerstone && window.cornerstoneWADOImageLoader && window.dicomParser) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-dicom-assets="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load DICOM viewer assets.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `${process.env.REACT_APP_BASE_API_URL || ''}${DICOM_ASSET_PATH}`;
    script.async = true;
    script.dataset.dicomAssets = 'true';
    script.onload = () => {
      if (window.cornerstone && window.cornerstoneWADOImageLoader && window.dicomParser) {
        resolve();
      } else {
        reject(new Error('DICOM viewer assets loaded but were unavailable.'));
      }
    };
    script.onerror = () => reject(new Error('Unable to load DICOM viewer assets.'));
    document.head.appendChild(script);
  });

const FileIcon = ({ kind }: { kind: ZipEntryKind }) => {
  const color = kind === 'dicom' ? '#4169F5' : kind === 'pdf' ? '#FF3030' : '#B9BEC7';
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M5.25 2.25H10.5L14.25 6V15.75H5.25V2.25Z" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10.5 2.25V6H14.25" stroke={color} strokeWidth="1.7" strokeLinejoin="round" />
      {kind === 'pdf' && <path d="M7.2 11.8V8.4H8.6C9.4 8.4 9.9 8.9 9.9 9.6C9.9 10.4 9.4 10.8 8.6 10.8H8V11.8H7.2ZM8 10.1H8.5C8.9 10.1 9.1 9.9 9.1 9.6C9.1 9.2 8.9 9.1 8.5 9.1H8V10.1Z" fill={color} />}
      {kind === 'dicom' && <path d="M7 11.25L8.35 9.75L9.35 10.8L10.5 9.45L12 11.25H7Z" fill={color} />}
      {kind === 'unsupported' && <path d="M7 8.25H11M7 10.5H10" stroke={color} strokeWidth="1.3" strokeLinecap="round" />}
    </svg>
  );
};

const FolderIcon = () => (
  <svg width="22" height="18" viewBox="0 0 22 18" fill="none" aria-hidden="true">
    <path d="M2.75 4.25C2.75 3.56 3.31 3 4 3H8.35L10.05 4.8H18C18.69 4.8 19.25 5.36 19.25 6.05V14C19.25 14.69 18.69 15.25 18 15.25H4C3.31 15.25 2.75 14.69 2.75 14V4.25Z" stroke="#4169F5" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M9 5.25C11.52 5.25 13.76 6.67 14.86 9C13.76 11.33 11.52 12.75 9 12.75C6.48 12.75 4.24 11.33 3.14 9C4.24 6.67 6.48 5.25 9 5.25Z" stroke="#7A7A7A" strokeWidth="1.7" />
    <circle cx="9" cy="9" r="1.7" stroke="#7A7A7A" strokeWidth="1.5" />
  </svg>
);

const DicomZipArchiveModal: React.FC<DicomZipArchiveModalProps> = ({ fileName, zipBlob, onClose }) => {
  const dicomElementRef = useRef<HTMLDivElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const [entries, setEntries] = useState<ZipViewerEntry[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [previewEntry, setPreviewEntry] = useState<ZipViewerEntry | null>(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Reading ZIP file...');
  const [errorMessage, setErrorMessage] = useState('');

  const summary = useMemo(() => collectSummary(entries), [entries]);
  const tree = useMemo(() => buildTree(entries), [entries]);
  const visibleTree = useMemo(() => filterTree(tree, searchTerm), [tree, searchTerm]);
  const allFolderIds = useMemo(() => getFolderIds(tree), [tree]);
  const supportedEntries = useMemo(() => entries.filter((entry) => entry.kind === 'dicom' || entry.kind === 'pdf'), [entries]);
  const selectedSupportedIndex = previewEntry ? supportedEntries.findIndex((entry) => entry.name === previewEntry.name) : -1;

  useEffect(() => {
    let cancelled = false;

    const readZip = async () => {
      try {
        const buffer = await zipBlob.arrayBuffer();
        const unzipped = unzipSync(new Uint8Array(buffer));
        if (cancelled) return;

        const nextEntries = Object.entries(unzipped)
          .filter(([name]) => !name.endsWith('/'))
          .map(([name, data]) => ({
            name,
            data,
            kind: getEntryKind(name, data),
            size: data.byteLength,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

        setEntries(nextEntries);
        setExpandedFolders(new Set(getFolderIds(buildTree(nextEntries))));
        setLoadingMessage('');
      } catch (error) {
        console.error('Unable to read ZIP file:', error);
        setErrorMessage('Unable to read this ZIP file.');
        setLoadingMessage('');
      }
    };

    readZip();

    return () => {
      cancelled = true;
    };
  }, [zipBlob]);

  useEffect(() => {
    if (objectUrlRef.current) {
      window.URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setViewerUrl('');
    setErrorMessage('');

    if (!previewEntry) return;

    const entryBuffer = new Uint8Array(previewEntry.data).buffer;
    const blob = new Blob([entryBuffer], {
      type: previewEntry.kind === 'pdf' ? 'application/pdf' : 'application/dicom',
    });
    const objectUrl = window.URL.createObjectURL(blob);
    objectUrlRef.current = objectUrl;

    if (previewEntry.kind === 'pdf') {
      setLoadingMessage('');
      setViewerUrl(objectUrl);
      return;
    }

    let cancelled = false;
    const element = dicomElementRef.current;
    const renderDicom = async () => {
      try {
        setLoadingMessage('Loading DICOM viewer...');
        await loadDicomAssets();
        if (cancelled || !element) return;

        const cornerstone = window.cornerstone;
        const wadouri = window.cornerstoneWADOImageLoader?.wadouri;
        window.cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
        window.cornerstoneWADOImageLoader.external.dicomParser = window.dicomParser;
        cornerstone.registerImageLoader('wadouri', wadouri.loadImage);
        cornerstone.enable(element);

        const image = await cornerstone.loadImage(`wadouri:${objectUrl}`);
        if (cancelled || !element) return;
        cornerstone.displayImage(element, image);
        setLoadingMessage('');
      } catch (error) {
        console.error('Unable to render DICOM file:', error);
        setErrorMessage('Unable to preview this DICOM file.');
        setLoadingMessage('');
      }
    };

    renderDicom();

    return () => {
      cancelled = true;
      if (element && window.cornerstone) {
        try {
          window.cornerstone.disable(element);
        } catch (_error) {
          // Cornerstone throws if the element was not enabled yet.
        }
      }
    };
  }, [previewEntry]);

  useEffect(
    () => () => {
      if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const selectRelativeSupportedEntry = (offset: number) => {
    if (!supportedEntries.length) return;
    const currentIndex = selectedSupportedIndex >= 0 ? selectedSupportedIndex : 0;
    const nextIndex = (currentIndex + offset + supportedEntries.length) % supportedEntries.length;
    setPreviewEntry(supportedEntries[nextIndex]);
  };

  const renderNode = (node: ZipTreeNode): React.ReactNode => {
    const isFolder = node.kind === 'folder';
    const isExpanded = isFolder && (expandedFolders.has(node.id) || Boolean(searchTerm.trim()));
    const folderSummary = isFolder ? collectSummary(getFolderEntries(node)) : null;
    const isUnsupported = node.entry?.kind === 'unsupported';
    const canPreview = node.entry?.kind === 'dicom' || node.entry?.kind === 'pdf';

    return (
      <React.Fragment key={node.id}>
        <div
          className={`tree-node ${isUnsupported ? 'unsupported' : 'supported'}`}
          style={{ paddingLeft: `${24 + Math.max(node.depth, 0) * 20}px` }}
        >
          <div className="node-name-wrapper">
            {isFolder ? (
              <button className="folder-toggle" onClick={() => toggleFolder(node.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}>
                <svg width="10" height="10" viewBox="0 0 14 14" fill="none" className={`arrow ${isExpanded ? '' : 'collapsed'}`}>
                  <path d="M3 5.25L7 9.25L11 5.25" stroke="#757575" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div style={{ transform: 'scale(0.8)' }}><FolderIcon /></div>
              </button>
            ) : (
              <span className="file-icon-wrapper">
                <FileIcon kind={node.entry?.kind || 'unsupported'} />
              </span>
            )}
            <span className={`node-name ${isFolder ? 'folder' : ''}`} title={node.name}>
              {node.name}
            </span>
          </div>
          <div className="node-summary">{folderSummary ? getFolderSummaryLabel(folderSummary) : ''}</div>
          <div className="node-preview">
            {canPreview && (
              <button onClick={() => setPreviewEntry(node.entry || null)} title="Preview file" aria-label={`Preview ${node.name}`}>
                <EyeIcon />
              </button>
            )}
          </div>
          <div className={`node-size ${isUnsupported ? 'unsupported' : 'supported'}`}>
            {node.entry ? formatBytes(node.entry.size) : ''}
          </div>
        </div>
        {isFolder && isExpanded && node.children.map(renderNode)}
      </React.Fragment>
    );
  };

  return (
    <div className="zip-modal-overlay">
      <div className="zip-modal-container">
        <div className="zip-modal-header">
          <div className="header-left">
            <div className="header-icon">
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <path d="M10 4H19L25 10V28H10V4Z" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
                <path d="M19 4V10H25" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
                <path d="M8 9H5V23H8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                <circle cx="15" cy="15" r="1.6" fill="currentColor" />
                <circle cx="20" cy="20" r="1.6" fill="currentColor" />
                <path d="M15 16.5V20H18.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="header-text-container">
              <h2>Archive contents</h2>
              <p>
                {fileName} · {formatBytes(zipBlob.size)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="close-button" aria-label="Close archive contents">
            <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
              <path d="M9 9L23 23M23 9L9 23" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="zip-modal-summary">
          <span className="summary-item"><div className="summary-item-icon"><FileIcon kind="dicom" /></div> {summary.dicom} DICOM</span>
          <span className="summary-item"><div className="summary-item-icon"><FileIcon kind="pdf" /></div> {summary.pdf} PDF</span>
          <span className="summary-item summary-other"><div className="summary-item-icon"><FileIcon kind="unsupported" /></div> {summary.unsupported} other</span>
          <span className="summary-dot">·</span>
          <span className="summary-total">{formatBytes(summary.size)} total</span>
        </div>

        <div className="zip-modal-search">
          <div className="search-input-wrapper">
            <svg width="18" height="18" viewBox="0 0 28 28" fill="none" aria-hidden="true">
              <circle cx="12.5" cy="12.5" r="8" stroke="currentColor" strokeWidth="2" />
              <path d="M18.5 18.5L24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              placeholder="Search files or folders..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="zip-modal-controls">
          <button onClick={() => setExpandedFolders(new Set(allFolderIds))}>Expand all</button>
          <button onClick={() => setExpandedFolders(new Set())}>Collapse all</button>
        </div>

        <div className="zip-modal-tree">
          {loadingMessage && !previewEntry && (
            <div className="tree-message loading">{loadingMessage}</div>
          )}
          {errorMessage && !previewEntry && (
            <div className="tree-message error">{errorMessage}</div>
          )}
          {!loadingMessage && !errorMessage && visibleTree.map(renderNode)}
        </div>

        <div className="zip-modal-footer">
          <button onClick={onClose}>Close</button>
        </div>
      </div>

      {previewEntry && (
        <div className="preview-modal-overlay">
          <div className="preview-modal-container">
            <div className="preview-modal-header">
              <p>{previewEntry.name}</p>
              <div className="preview-controls">
                <button className="nav-btn" onClick={() => selectRelativeSupportedEntry(-1)}>
                  Previous
                </button>
                <button className="nav-btn" onClick={() => selectRelativeSupportedEntry(1)}>
                  Next
                </button>
                <button onClick={() => setPreviewEntry(null)} className="close-btn" aria-label="Close preview">
                  ×
                </button>
              </div>
            </div>
            <div className="preview-modal-body">
              {loadingMessage && (
                <div className="preview-message loading">
                  {loadingMessage}
                </div>
              )}
              {errorMessage && (
                <div className="preview-message error">
                  {errorMessage}
                </div>
              )}
              {previewEntry.kind === 'pdf' && viewerUrl && <iframe src={viewerUrl} title={previewEntry.name} />}
              {previewEntry.kind === 'dicom' && <div ref={dicomElementRef} className="dicom-viewer" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DicomZipArchiveModal;
